import { createAsyncThunk, createSlice } from '@reduxjs/toolkit';
import { apiClient } from '../services/apiClient';

export interface TenantCounts {
  tenantId: string;
  tenantName: string;
  totalSITs: number;
  deployedSITs: number;
  undeployedSITs: number;
  sitDeploymentPercentage: number;
  totalLabels: number;
  deployedLabels: number;
  undeployedLabels: number;
  labelDeploymentPercentage: number;
  totalPolicies: number;
  deployedPolicies: number;
  undeployedPolicies: number;
  policyDeploymentPercentage: number;
  totalEntities: number;
  totalDeployedEntities: number;
  overallDeploymentPercentage: number;
}

interface TenantCountsState {
  data: Record<string, TenantCounts>; // keyed by tenantId
  loading: boolean;
  error: string | null;
}

const initialState: TenantCountsState = {
  data: {},
  loading: false,
  error: null,
};

// Async thunk for fetching tenant counts
export const fetchTenantCounts = createAsyncThunk(
  'tenantCounts/fetchTenantCounts',
  async (tenantId: string, { rejectWithValue }) => {
    try {
      const client = apiClient();
      const response = await client.get<TenantCounts>(`tenants/${tenantId}/entities/counts`);
      return { tenantId, data: response.data };
    } catch (error: any) {
      return rejectWithValue(error.response?.data?.message || 'Failed to fetch tenant counts');
    }
  }
);

const tenantCountsSlice = createSlice({
  name: 'tenantCounts',
  initialState,
  reducers: {
    clearError: (state) => {
      state.error = null;
    },
    clearTenantCounts: (state) => {
      state.data = {};
    },
  },
  extraReducers: (builder) => {
    builder
      .addCase(fetchTenantCounts.pending, (state) => {
        state.loading = true;
        state.error = null;
      })
      .addCase(fetchTenantCounts.fulfilled, (state, action) => {
        state.loading = false;
        state.data[action.payload.tenantId] = action.payload.data;
      })
      .addCase(fetchTenantCounts.rejected, (state, action) => {
        state.loading = false;
        state.error = action.payload as string;
      });
  },
});

export const { clearError, clearTenantCounts } = tenantCountsSlice.actions;

// Selectors
export const selectTenantCounts = (state: { tenantCounts: TenantCountsState }) => state.tenantCounts.data;
export const selectTenantCountsLoading = (state: { tenantCounts: TenantCountsState }) => state.tenantCounts.loading;
export const selectTenantCountsError = (state: { tenantCounts: TenantCountsState }) => state.tenantCounts.error;
export const selectTenantCountsForTenant = (tenantId: string) => 
  (state: { tenantCounts: TenantCountsState }) => state.tenantCounts.data[tenantId];

export default tenantCountsSlice.reducer;